import React, { useEffect, useState } from 'react';
import axios from 'axios';
import srp from 'secure-remote-password/client';
import { useAuth } from './contexts/AuthContext';
import { decryptFile } from './utils/crypto';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5005';

function DownloadPage() {
  const { isAuthenticated } = useAuth();
  const [decryptedBlobUrl, setDecryptedBlobUrl] = useState(null);
  const [status, setStatus] = useState('Loading...');
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [srpSession, setSrpSession] = useState(null);
  const [downloadToken, setDownloadToken] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [key, setKey] = useState(null);
  const [tokenParam, setTokenParam] = useState(null);
  const [csrfToken, setCsrfToken] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/csrf-token`).then(() => {
      const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
      if (match) setCsrfToken(match[1]);
    }).catch(() => {});
  }, []);

  // Helper function for download and decryption
  // tokenOverride/keyOverride allow passing values directly when state hasn't updated yet
  const fetchAndDecrypt = async (tokenOverride = null, keyOverride = null) => {
    const tokenToUse = tokenOverride || tokenParam;
    const keyToUse = keyOverride || key;
    if (!tokenToUse) {
      setError('Invalid download link.');
      setStatus('');
      return;
    }
    try {
      setStatus('Downloading encrypted file...');
      // SRP authentication if password is required
      if (showPasswordPrompt && password && !downloadToken) {
        setStatus('Authenticating with SRP...');
        try {
          // 1. Get challenge and salt from backend
          const challengeRes = await axios.get(`${API_URL}/srp/challenge?fileId=${fileId}`);
          const { salt, B } = challengeRes.data;
          // 2. Generate SRP client ephemeral
          // Note: generatePrivateKey might not be available, use an alternative method or library version workaround
          let privateKey;
          if (srp.generateEphemeral) {
            privateKey = srp.generateEphemeral().secret;
          } else {
            const randomBytes = new Uint8Array(32);
            crypto.getRandomValues(randomBytes);
            privateKey = Array.from(randomBytes, b => b.toString(16).padStart(2, '0')).join('');
          }
          const publicKey = srp.derivePublicKey ? srp.derivePublicKey(privateKey) : privateKey; // Fallback if derivePublicKey is not available
          // 3. Compute M1 proof
          const M1 = srp.deriveProof(
            Buffer.from(salt, 'hex'),
            fileId,
            password,
            privateKey,
            Buffer.from(B, 'hex')
          );
          // 4. Send proof to backend
          const proofRes = await axios.post(`${API_URL}/srp/proof`, {
            fileId,
            A: publicKey.toString('hex'),
            M1: M1.toString('hex'),
          }, {
            headers: { 'X-CSRF-Token': csrfToken }
          });
          // 5. If successful, store downloadToken and proceed
          setSrpSession({ authenticated: true });
          setDownloadToken(proofRes.data.downloadToken);
          // Now fetch the file with the downloadToken
          setStatus('Downloading encrypted file...');
          const response = await axios.post(`${API_URL}/api/download`, {
            token: tokenToUse,
            downloadToken: proofRes.data.downloadToken
          });
          if (!response.data || !response.data.encryptedData || !response.data.originalName) {
            setError('File not found, expired, or server error.');
            setStatus('');
            return;
          }
          const encryptedText = response.data.encryptedData.trim();
          setStatus('Decrypting file...');
          // decryptFile handles both legacy CBC and new GCM formats
          const typedArray = await decryptFile(encryptedText, keyToUse);
          const blob = new Blob([typedArray]);
          let filename = response.data.originalName || 'decrypted_file';
          const blobUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setStatus('File downloaded successfully.');
          setDecryptedBlobUrl(null);
          return;
        } catch (authErr) {
          console.error('SRP Authentication error:', authErr);
          setError('Authentication failed. Incorrect password or server error.');
          setStatus('');
          setShowPasswordPrompt(true);
          return;
        }
      }
      // If not password-protected or already have downloadToken, fetch as usual
      const response = await axios.post(`${API_URL}/api/download`, {
        token: tokenToUse,
        ...(downloadToken ? { downloadToken } : {})
      });
      if (!response.data || !response.data.encryptedData || !response.data.originalName) {
        setError('File not found, expired, or server error.');
        setStatus('');
        return;
      }
      const encryptedText = response.data.encryptedData.trim();
      setStatus('Decrypting file...');
      // decryptFile handles both legacy CBC and new GCM formats
      const typedArray = await decryptFile(encryptedText, keyToUse);
      const blob = new Blob([typedArray]);
      let filename = response.data.originalName || 'decrypted_file';
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setStatus('File downloaded successfully.');
      setDecryptedBlobUrl(null);
    } catch (err) {
      console.error('Download error:', err);
      if (err.response && err.response.status === 401) {
        setShowPasswordPrompt(true);
        setStatus('Password required to download this file.');
      } else if (err.response && (err.response.status === 404 || err.response.status === 410)) {
        setError(err.response.data?.text || 'File not found or expired.');
        setStatus('');
      } else {
        setError('Failed to download or decrypt the file. ' + (err.message || 'Unknown error.'));
        setStatus('');
      }
    }
  };

  useEffect(() => {
    // Token lives in the URL fragment (#) so it's never sent to the server just by
    // opening this page. Query string (?) is still read for older links already
    // shared before this change.
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const searchParams = new URLSearchParams(window.location.search);
    const tokenParamValue = hashParams.get('token') || searchParams.get('token');
    const token = tokenParamValue ? decodeURIComponent(tokenParamValue) : null;
    setTokenParam(tokenParamValue);
    // Convert URL-safe Base64 back to standard Base64
    let base64 = token ? token.replace(/-/g, '+').replace(/_/g, '/') : '';
    while (base64.length % 4) base64 += '=';
    if (!token) {
      setError('Invalid download link.');
      setStatus('');
      return;
    }
    let fileIdValue, keyValue;
    try {
      [fileIdValue, keyValue] = atob(base64).split(':');
      setFileId(fileIdValue);
      setKey(keyValue);
    } catch {
      setError('Invalid download link.');
      setStatus('');
      return;
    }
    // Check if file is password protected
    axios.get(`${API_URL}/srp/challenge?fileId=${fileIdValue}`).then(response => {
      if (response.data && response.data.salt && response.data.B) {
        setShowPasswordPrompt(true);
        setStatus('Password required to download this file.');
      } else {
        fetchAndDecrypt(tokenParamValue, keyValue);
      }
    }).catch(err => {
      if (err.response && err.response.status === 404) {
        fetchAndDecrypt(tokenParamValue, keyValue);
      } else if (err.response && (err.response.status === 410)) {
        setError(err.response.data?.text || 'File expired.');
        setStatus('');
      } else {
        setError('Failed to verify file status.');
        setStatus('');
      }
    });
    return () => {
      if (decryptedBlobUrl) {
        URL.revokeObjectURL(decryptedBlobUrl.url);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setStatus('Verifying password...');
    setShowPasswordPrompt(false);
    await new Promise(r => setTimeout(r, 200)); // slight delay for UX
    await fetchAndDecrypt();
  };

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ color: 'red', marginBottom: '12px', fontWeight: 'bold' }}>
          {error}
        </div>
        <a href={isAuthenticated ? "/dashboard" : "/"} style={{ color: '#2186EB', textDecoration: 'underline' }}>
          {isAuthenticated ? "Go to Dashboard" : "Return Home"}
        </a>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <h2>Secure File Download</h2>
      <p>This file is encrypted for your privacy. It will be decrypted locally in your browser.</p>
      <p>{status}</p>
      {showPasswordPrompt && (
        <form onSubmit={handlePasswordSubmit} style={{ margin: '20px auto', maxWidth: '300px' }}>
          <label style={{ display: 'none' }}>
            Username (for accessibility):
            <input
              type="text"
              value={fileId}
              readOnly
              autoComplete="username"
              style={{ display: 'none' }}
            />
          </label>
          <label>
            Enter Password:
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={{ marginLeft: '8px', width: '160px' }}
              required
              autoComplete="new-password"
            />
          </label>
          <button type="submit" style={{ marginLeft: '10px' }}>Submit</button>
        </form>
      )}
      {!decryptedBlobUrl && !error && !showPasswordPrompt && status !== 'File downloaded successfully.' && (
        <button onClick={() => fetchAndDecrypt()} style={{ fontSize: '18px', color: '#ff7417', marginTop: '20px' }}>
          Click here to download your decrypted file
        </button>
      )}
      {status === 'File downloaded successfully.' && (
        <div style={{ marginTop: '20px' }}>
          <div style={{ color: 'green', fontWeight: 'bold' }}>
            File downloaded successfully.
          </div>
          <a href={isAuthenticated ? "/dashboard" : "/"} style={{ color: '#2186EB', textDecoration: 'underline', display: 'inline-block', marginTop: '12px' }}>
            {isAuthenticated ? "Go to Dashboard" : "Share your own files"}
          </a>
        </div>
      )}
    </div>
  );
}

export default DownloadPage;
